import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Column } from 'primereact/column';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { FilterMatchMode } from 'primereact/api';
import { renderCell } from '../../component/data-table/operations/render-cell';
import { getCellForCourseStatus } from '../programs/program/courses/operations/get-cell-for-course-status';
import { CoursesFilter, CourseFilters } from './courses-filter/courses-filter';
import { getCoursesListOperation } from './operations/get-courses-list-operation';
import dateFormat from '../../utils/date-format';
import navigationManager from '../../utils/navigation-manager';
import { ReportCoursesType } from '../../api/types/reports/education/courses/report-courses.type';
import { TableColumnLongTextWithInformation } from '../../component/table-componenets/cusom-column/table-column-long-text-with-information';
import { toast } from 'react-toastify';

interface CoursesListPageProps {
  allowNavigation?: boolean;
  onCourseClick?: (course: ReportCoursesType) => any;
}

export const CoursesListPage: React.FC<CoursesListPageProps> = ({
  allowNavigation = true,
  onCourseClick,
}) => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<ReportCoursesType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (allowNavigation) {
      navigationManager.setNavigate(navigate);
    }
  }, [navigate, allowNavigation]);

  const fetchCourses = async (filters: CourseFilters) => {
    const res = await getCoursesListOperation(filters, setLoading, setCourses);
  };

  const handleRowClick = (rowData: ReportCoursesType) => {
    if (!allowNavigation && onCourseClick) {
      onCourseClick(rowData);
      return;
    }

    if (rowData.courseProgramYear === null) {
      toast.info('קורס זה נמצא ביתרה לניצול, יש לשייך לשנה טרם הכניסה לקורס');
    } else {
      navigationManager.navigateToCourse(
        rowData.branchId,
        rowData.programs[0].programId,
        rowData.courseId,
      );
    }
  };

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    courseName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    branchName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    majorName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    abstractProgramName: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const handleFilterChange = (courseFilters: CourseFilters) => {
    fetchCourses(courseFilters);
  };

  const formatLecturers = (lecturers: any[]) => {
    return (
      lecturers?.map((l) => `${l.firstName} ${l.lastName}`).join(', ') || ''
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '20px',
        paddingTop: '16px',
        height: 'calc(100vh - 80px)',
      }}
    >
      {/* Filter Section */}
      <div
        style={{
          height: 'calc(100vh - 72px)',
          width: '300px',
          minWidth: '250px',
        }}
      >
        <CoursesFilter onFilterChange={handleFilterChange} />
      </div>

      {/* Table Section */}
      <div style={{ flex: 1 }}>
        <div
          style={{
            color: '#041E49',
            fontSize: '22px',
            fontWeight: 'bold',
            marginBottom: '20px',
          }}
        >
          רשימת קורסים
        </div>

        <DataTable
          value={courses}
          loading={loading}
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
          }
          dataKey="courseId"
          tableStyle={{
            direction: 'rtl',
            cursor: allowNavigation ? 'pointer' : 'default',
          }}
          filterDisplay="row"
          filters={filters}
          onFilter={(e) => setFilters(e.filters)}
          scrollable
          scrollHeight="calc(100vh - 120px)"
          onRowClick={(e) => handleRowClick(e.data as ReportCoursesType)}
        >
          <Column
            field="branchName"
            header="שלוחה"
            filter
            filterPlaceholder="חפש שלוחה"
            style={{ textAlign: 'right', maxWidth: '108px' }}
          />
          <Column
            field="courseName"
            header="שם קורס"
            filter
            filterPlaceholder="חפש קורס"
            style={{
              textAlign: 'right',
              maxWidth: '108px',
            }}
          />
          {/*          <Column
            field="majorName"
            header="מגמה"
            filter
            filterPlaceholder="חפש מגמה"
            style={{ textAlign: 'right' }}
          />*/}
          <Column
            field="abstractProgramName"
            header="שם תכנית"
            filter
            filterPlaceholder="חפש תכנית"
            style={{ textAlign: 'right' }}
            body={(rowData: ReportCoursesType) => {
              const firstProgram = rowData.programs?.[0];
              return firstProgram
                ? `${firstProgram.abstractProgramName} - מחזור ${firstProgram.annualSerialProgramNumber} - ${firstProgram.programStartYear}`
                : '';
            }}
          />
          {/*<Column
            field="programStartYear"
            header="שנת התחלה"
            sortable
            style={{ textAlign: 'right' }}
          />
          <Column
            field="annualSerialProgramNumber"
            header="מספר סידורי בשנה"
            sortable
            style={{ textAlign: 'right' }}
          />
          <Column
            field="abstractProgramYears"
            header="שנה בתכנית"
            sortable
            style={{ textAlign: 'right' }}
            body={(rowData) => `שנה ${rowData.abstractProgramYears}'`}
          />*/}
          <Column
            field="courseStartDate"
            header="תאריך התחלה"
            sortable
            style={{ textAlign: 'right' }}
            body={(rowData: ReportCoursesType) => (
              <label>
                {rowData.courseStartDate
                  ? dateFormat.displayFormatDate(rowData.courseStartDate)
                  : 'טרם שובצו הרצאות'}
              </label>
            )}
          />
          <Column
            field="status"
            header="סטטוס קורס"
            style={{ textAlign: 'right' }}
            body={(rowData) =>
              renderCell(getCellForCourseStatus(rowData.status))
            }
          />
          <Column
            field="courseHours"
            header="מכסת שעות מקורית"
            sortable
            style={{ textAlign: 'right', maxWidth: '85px' }}
          />
          <Column
            field="capacityHours"
            header="מכסת שעות עדכנית"
            sortable
            style={{ textAlign: 'right', maxWidth: '85px' }}
          />

          <Column
            field="scheduledHours"
            header="סך שעות ששובצו"
            sortable
            style={{ textAlign: 'right', maxWidth: '85px' }}
          />
          <Column
            field="hoursActuallyWorked"
            header="סך שעות שבוצעו"
            sortable
            style={{ textAlign: 'right', maxWidth: '85px' }}
          />
          <Column
            field="attendancePercentage"
            header="אחוז נוכחות"
            sortable
            style={{ textAlign: 'right', maxWidth: '85px' }}
          />
          <Column
            field="studentNumber"
            header="מספר תלמידים"
            sortable
            style={{ textAlign: 'right', maxWidth: '85px' }}
          />
          <Column
            field="averageFinalGrade"
            header="ציון סופי ממוצע"
            sortable
            style={{ textAlign: 'right', maxWidth: '85px' }}
          />
          <Column
            field="lecturers"
            header="מרצים"
            style={{ textAlign: 'right' }}
            body={(rowData) => formatLecturers(rowData.lecturers)}
          />
          <Column
            field="courseNotes"
            header="הערות הקורס"
            style={{ textAlign: 'right' }}
            body={(rowData: ReportCoursesType) => (
              <TableColumnLongTextWithInformation
                text={rowData.courseNotes}
                uniqueId={rowData.courseId}
                maxLength={8}
              />
            )}
          />
        </DataTable>
      </div>
    </div>
  );
};
